/**
 * @documental
 * @export Body
 * @import
 * import { Body } from "capitalrx-components-2.0/lib/typography";
 */
import { FC, ReactNode, useContext, useMemo } from "react";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { TextAlignment } from "../TextAlignment";
import { BodyContext, BodyContextValue } from "./BodyContext";

import styles from "./styles.module.scss";

/**
 * The Body component properties.
 */
export interface BodyProps extends BaseProps {
  /**
   * Specifies the size of the body text. If not specified defaults to "small". If set to "inherit" then the
   * body element will inherit the size of the first parent body element.
   */
  readonly size?: "inherit" | "medium" | "small" | "mini";

  /**
   * The text alignment. If not specified defaults to `"left"`.
   */
  readonly textAlign?: TextAlignment;

  /**
   * Indicates whether to render the body as an inline element instead of as a block element.
   */
  readonly inline?: boolean;

  readonly children: ReactNode | string;

  /**
   * The ID of the component the body is for.
   */
  readonly forId?: string;
}

/**
 * Specifies a section of content which should be displayed as regular body text, and allows for you to specify
 * the text size. When Body is used, content text is reset to the [Regular](../Regular/README.md) text style.
 */
export const Body: FC<BodyProps> = (props) => {
  const { children, size = "small", textAlign = "left", inline = false, forId } = props;

  const parentBodyContext = useContext(BodyContext);
  const finalSize = size === "inherit" ? (parentBodyContext?.size ?? "medium") : size;
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, { className: [styles.Body, "body", finalSize, `align-${textAlign}`] });

  const contextValue = useMemo(
    (): BodyContextValue => ({
      size: finalSize,
    }),
    [finalSize],
  );

  return (
    <BodyContext.Provider value={contextValue}>
      {inline && <span id={forId} {...baseHtmlProps}>{children}</span>}
      {!inline && <div id={forId} {...baseHtmlProps}>{children}</div>}
    </BodyContext.Provider>
  );
};
